/**
 * Saldo a favor de clientes (CxC).
 *
 * El cliente depositó de más y pidió dejarlo a favor: el excedente queda guardado y después
 * se aplica a una o varias de sus facturas (misma moneda) o se le devuelve.
 *
 *  - BloqueExcedente: el bloque opcional de los modales de cobro («el depósito trajo de más»)
 *  - RegistrarSaldoFavorModal: registrar después del cobro (o un anticipo)
 *  - SaldoFavorModal: aplicar a facturas, historial de movimientos y devolución
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Wallet, ArrowDownToLine, Undo2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { cxcApi } from '../../utils/api';

const fmt = (v, mon) => {
  const n = Number(v) || 0;
  return (mon === 'USD' ? '$' : '₡') + n.toLocaleString(mon === 'USD' ? 'en-US' : 'es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const MEDIOS = [['transferencia', 'Transferencia'], ['sinpe', 'SINPE Móvil'], ['cheque', 'Cheque'], ['efectivo', 'Efectivo'], ['tarjeta', 'Tarjeta'], ['otro', 'Otro']];
const etiquetaTipo = {
  excedente: ['Excedente', 'border-emerald-500 text-emerald-700'],
  registro: ['Saldo a favor', 'border-emerald-500 text-emerald-700'],
  aplicacion: ['Aplicado', 'border-indigo-400 text-indigo-700'],
  devolucion: ['Devuelto', 'border-red-300 text-red-600']
};

/** Chip para la lista de clientes: «A favor $80,90» (una o dos monedas) */
export function ChipSaldoFavor({ saldo }) {
  if (!saldo) return null;
  const partes = Object.entries(saldo).filter(([, v]) => v > 0).map(([m, v]) => fmt(v, m));
  if (!partes.length) return null;
  return (
    <span className="flex-shrink-0 px-1.5 py-0.5 border border-emerald-500 text-emerald-700 bg-white text-[11px] font-semibold rounded whitespace-nowrap" title="Saldo a favor del cliente">
      A favor {partes.join(' · ')}
    </span>
  );
}

/**
 * Bloque opcional de los modales de cobro. `cobrado` es lo que se registra como cobro en la
 * moneda del depósito; el excedente = depósito − cobrado. Avisa por onChange({excedenteFavor, excedenteMoneda, excedenteNota}).
 */
export function BloqueExcedente({ moneda, cobrado, onChange }) {
  const [activo, setActivo] = useState(false);
  const [deposito, setDeposito] = useState('');
  const [nota, setNota] = useState('Confirmado por el cliente');
  const excedente = activo ? r2((parseFloat(deposito) || 0) - (Number(cobrado) || 0)) : 0;
  useEffect(() => {
    onChange(activo && excedente > 0 ? { excedenteFavor: excedente, excedenteMoneda: moneda, excedenteNota: nota } : null);
  }, [activo, excedente, moneda, nota]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className={`mt-4 p-3 border rounded-lg ${activo ? 'border-emerald-500' : 'border-emerald-300'}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} className="rounded border-gray-300 accent-emerald-600" />
        <span className="text-sm font-medium text-emerald-800">Excedente: registrar saldo a favor</span>
      </label>
      {activo && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="min-w-0">
              <label className="block text-xs font-medium text-gray-700 mb-1 whitespace-nowrap">Depósito ({moneda === 'USD' ? '$' : '₡'})</label>
              <input type="number" step="0.01" value={deposito} onChange={(e) => setDeposito(e.target.value)} placeholder="0.00"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500" />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-medium text-gray-700 mb-1 whitespace-nowrap">Excedente a favor</label>
              <div className={`border rounded-lg px-3 py-2 text-sm font-semibold truncate ${excedente > 0 ? 'border-emerald-500 text-emerald-700' : 'text-gray-400'}`}>
                {excedente > 0 ? fmt(excedente, moneda) : '—'}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nota</label>
            <input type="text" value={nota} onChange={(e) => setNota(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500" />
          </div>
          <p className={`text-xs ${deposito && excedente <= 0 ? 'text-red-600' : 'text-gray-500'}`}>
            {deposito && excedente <= 0 ? 'El depósito tiene que ser mayor que lo cobrado.' : 'Excedente = depósito − cobrado.'}
          </p>
        </div>
      )}
    </div>
  );
}

/** Registrar un saldo a favor después del cobro (el cliente confirmó días después) */
export function RegistrarSaldoFavorModal({ cliente, bancos = [], onClose, onSuccess }) {
  const [form, setForm] = useState({ monto: '', moneda: 'USD', fecha: dayjs().format('YYYY-MM-DD'), medioPago: 'transferencia', banco: '', numeroComprobante: '', notas: '' });
  const [archivo, setArchivo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const guardar = async (e) => {
    e.preventDefault();
    if (!(parseFloat(form.monto) > 0)) { toast.error('Indique el monto del saldo a favor'); return; }
    setGuardando(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, receptorId: cliente.id }).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (archivo) fd.append('comprobante', archivo);
      const r = await cxcApi.registrarSaldoFavor(fd);
      toast.success(`Saldo a favor de ${fmt(form.monto, form.moneda)} registrado`);
      if (r.data?.data?.asientoError) toast(`Registrado, pero falló el asiento contable: ${r.data.data.asientoError}`, { icon: '⚠️', duration: 8000 });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo registrar el saldo a favor');
    } finally { setGuardando(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={guardar} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl border border-emerald-400 flex items-center justify-center"><Wallet className="h-4 w-4 text-emerald-600" /></span>
            <div><h3 className="text-lg font-semibold text-gray-900">Registrar saldo a favor</h3><p className="text-xs text-gray-500">{cliente.nombre} · {cliente.identificacion}</p></div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
          <div><label className="block font-medium text-gray-700 mb-1">Monto *</label>
            <input type="number" step="0.01" value={form.monto} onChange={set('monto')} required className="w-full border rounded-lg px-3 py-2" /></div>
          <div><label className="block font-medium text-gray-700 mb-1">Moneda *</label>
            <select value={form.moneda} onChange={set('moneda')} className="w-full border rounded-lg px-3 py-2"><option value="USD">Dólares (USD)</option><option value="CRC">Colones (CRC)</option></select></div>
          <div><label className="block font-medium text-gray-700 mb-1">Fecha del depósito *</label>
            <input type="date" value={form.fecha} onChange={set('fecha')} required className="w-full border rounded-lg px-3 py-2" /></div>
          <div><label className="block font-medium text-gray-700 mb-1">Medio de pago</label>
            <select value={form.medioPago} onChange={set('medioPago')} className="w-full border rounded-lg px-3 py-2">{MEDIOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
          <div><label className="block font-medium text-gray-700 mb-1">Banco</label>
            <select value={form.banco} onChange={set('banco')} className="w-full border rounded-lg px-3 py-2"><option value="">-- Seleccionar --</option>{bancos.map(b => <option key={b.id} value={b.nombre}>{b.nombre}</option>)}</select></div>
          <div><label className="block font-medium text-gray-700 mb-1">N° Comprobante / Referencia</label>
            <input type="text" value={form.numeroComprobante} onChange={set('numeroComprobante')} className="w-full border rounded-lg px-3 py-2" /></div>
          <div className="sm:col-span-2"><label className="block font-medium text-gray-700 mb-1">Comprobante (opcional)</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setArchivo(e.target.files?.[0] || null)} className="w-full border rounded-lg px-2 py-1.5 text-sm" /></div>
          <div className="sm:col-span-2"><label className="block font-medium text-gray-700 mb-1">Nota</label>
            <input type="text" value={form.notas} onChange={set('notas')} placeholder="Ej: Excedente del depósito 950471814, confirmado por el cliente" className="w-full border rounded-lg px-3 py-2" /></div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100">Cancelar</button>
          <button type="submit" disabled={guardando} className="px-4 py-2 border-2 border-emerald-600 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-2">
            {guardando && <Loader2 className="h-4 w-4 animate-spin" />} Registrar saldo a favor
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Saldo a favor del cliente: aplicar a facturas, historial y devolución.
 * facturas: las pendientes del cliente (de la pantalla de CxC) con { id, consecutivo, moneda, saldoPendiente, fechaVencimiento }
 */
export function SaldoFavorModal({ cliente, facturas = [], bancos = [], vistaInicial = 'aplicar', onClose, onSuccess }) {
  const [vista, setVista] = useState(vistaInicial);
  const [datos, setDatos] = useState(null);
  const [moneda, setMoneda] = useState(null);
  const [marcadas, setMarcadas] = useState({});
  const [trabajando, setTrabajando] = useState(false);
  const [dev, setDev] = useState({ monto: '', fecha: dayjs().format('YYYY-MM-DD'), medioPago: 'transferencia', banco: '', numeroComprobante: '', notas: '' });

  const cargar = async () => {
    try {
      const r = await cxcApi.saldoFavor(cliente.id);
      setDatos(r.data.data);
      setMoneda(m => m || Object.keys(r.data.data.saldo || {})[0] || 'USD');
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo cargar el saldo a favor'); }
  };
  useEffect(() => { cargar(); }, [cliente.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const disponible = datos?.saldo?.[moneda]?.disponible || 0;
  const pendientes = useMemo(() => facturas.filter(f => (f.moneda || 'CRC') === moneda && Number(f.saldoPendiente) > 0)
    .sort((a, b) => new Date(a.fechaVencimiento || 0) - new Date(b.fechaVencimiento || 0)), [facturas, moneda]);
  const totalAplicar = r2(Object.values(marcadas).reduce((s, v) => s + (parseFloat(v) || 0), 0));

  // Marcar reparte lo que queda disponible, de la más vieja a la más nueva
  const alternar = (f) => {
    setMarcadas(prev => {
      const n = { ...prev };
      if (f.id in n) { delete n[f.id]; return n; }
      const usado = Object.values(n).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      n[f.id] = r2(Math.max(0, Math.min(Number(f.saldoPendiente), disponible - usado))).toFixed(2);
      return n;
    });
  };

  const aplicar = async () => {
    const aplicaciones = Object.entries(marcadas).map(([id, m]) => ({ documentoId: Number(id), monto: parseFloat(m) || 0 })).filter(a => a.monto > 0);
    if (!aplicaciones.length) { toast.error('Marque al menos una factura'); return; }
    if (totalAplicar > disponible + 0.004) { toast.error(`Solo hay ${fmt(disponible, moneda)} a favor`); return; }
    setTrabajando(true);
    try {
      const r = await cxcApi.aplicarSaldoFavor({ receptorId: cliente.id, moneda, aplicaciones });
      toast.success(r.data?.message || 'Saldo a favor aplicado');
      const fallas = (r.data?.data?.aplicaciones || []).filter(a => a.asientoError);
      if (fallas.length) toast(`Aplicado, pero falló el asiento contable: ${fallas[0].asientoError}`, { icon: '⚠️', duration: 8000 });
      setMarcadas({});
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo aplicar el saldo a favor'); }
    finally { setTrabajando(false); }
  };

  const devolver = async () => {
    const monto = parseFloat(dev.monto) || 0;
    if (!(monto > 0)) { toast.error('Indique el monto a devolver'); return; }
    if (monto > disponible + 0.004) { toast.error(`Solo hay ${fmt(disponible, moneda)} a favor`); return; }
    setTrabajando(true);
    try {
      const r = await cxcApi.devolverSaldoFavor({ receptorId: cliente.id, moneda, ...dev, monto });
      toast.success(`Devolución de ${fmt(monto, moneda)} registrada`);
      if (r.data?.data?.asientoError) toast(`Registrada, pero falló el asiento contable: ${r.data.data.asientoError}`, { icon: '⚠️', duration: 8000 });
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo registrar la devolución'); }
    finally { setTrabajando(false); }
  };

  const monedas = Object.keys(datos?.saldo || {});
  const movs = (datos?.movimientos || []).filter(m => m.moneda === moneda);
  let corrido = 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-5xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl border border-emerald-400 flex items-center justify-center"><Wallet className="h-4 w-4 text-emerald-600" /></span>
            <div><h3 className="text-lg font-semibold text-gray-900">Saldo a favor · {cliente.nombre}</h3><p className="text-xs text-gray-500">{cliente.identificacion}</p></div>
          </div>
          <div className="flex items-center gap-4">
            {monedas.length > 1 && (
              <div className="inline-flex rounded-lg p-0.5 gap-0.5 border border-gray-200">
                {monedas.map(m => <button key={m} onClick={() => { setMoneda(m); setMarcadas({}); }} className={`px-3 py-1 rounded text-xs font-semibold ${moneda === m ? 'border border-emerald-500 text-emerald-700' : 'text-gray-500'}`}>{m}</button>)}
              </div>
            )}
            <div className="text-right"><p className="text-xs text-gray-500">Disponible</p><p className="text-2xl font-bold text-emerald-700">{datos ? fmt(disponible, moneda) : '…'}</p></div>
            <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex gap-1 px-6 pt-3 border-b">
          {[['aplicar', 'Aplicar a facturas', ArrowDownToLine], ['historial', 'Historial', CheckCircle2], ['devolver', 'Devolver', Undo2]].map(([v, t, I]) => (
            <button key={v} onClick={() => setVista(v)} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px ${vista === v ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}><I className="h-4 w-4" /> {t}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {!datos ? <div className="py-16 text-center text-gray-500"><Loader2 className="h-6 w-6 animate-spin inline" /></div> : vista === 'aplicar' ? (
            pendientes.length === 0 ? <p className="p-8 text-center text-sm text-gray-500">El cliente no tiene facturas pendientes en {moneda}. El saldo a favor solo se aplica a facturas de la misma moneda.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600"><tr>
                  <th className="px-4 py-3 w-10"></th><th className="px-4 py-3 text-left">Factura</th><th className="px-4 py-3 text-left">Vencimiento</th>
                  <th className="px-4 py-3 text-right">Saldo</th><th className="px-4 py-3 text-right">Aplicar</th><th className="px-4 py-3 text-right">Queda</th></tr></thead>
                <tbody className="divide-y">
                  {pendientes.map(f => {
                    const on = f.id in marcadas;
                    const queda = r2(Number(f.saldoPendiente) - (parseFloat(marcadas[f.id]) || 0));
                    return (
                      <tr key={f.id} className={on ? 'bg-emerald-50/40' : ''}>
                        <td className="px-4 py-3"><input type="checkbox" checked={on} onChange={() => alternar(f)} className="accent-emerald-600" /></td>
                        <td className="px-4 py-3 font-mono text-xs">{f.consecutivo}</td>
                        <td className="px-4 py-3">{f.fechaVencimiento ? dayjs(f.fechaVencimiento).format('DD/MM/YYYY') : '—'}</td>
                        <td className="px-4 py-3 text-right">{fmt(f.saldoPendiente, moneda)}</td>
                        <td className="px-4 py-3 text-right">
                          {on ? <input type="number" step="0.01" value={marcadas[f.id]} onChange={(e) => setMarcadas({ ...marcadas, [f.id]: e.target.value })}
                            className="w-28 border border-emerald-500 rounded px-2 py-1 text-right font-semibold text-emerald-700" /> : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">{fmt(Math.max(0, queda), moneda)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : vista === 'historial' ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600"><tr><th className="px-4 py-3 text-left">Fecha</th><th className="px-4 py-3 text-left">Movimiento</th><th className="px-4 py-3 text-left">Detalle</th><th className="px-4 py-3 text-right">Monto</th><th className="px-4 py-3 text-right">Disponible</th></tr></thead>
              <tbody className="divide-y">
                {movs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Sin movimientos en {moneda}.</td></tr>}
                {movs.map(m => {
                  const entra = m.tipo === 'excedente' || m.tipo === 'registro';
                  corrido = r2(corrido + (entra ? 1 : -1) * Number(m.monto));
                  const [t, c] = etiquetaTipo[m.tipo] || [m.tipo, 'border-gray-300 text-gray-600'];
                  const detalle = m.tipo === 'aplicacion' ? `A la FE ${m.factura_consecutivo || ''}`
                    : [m.numero_comprobante && `Comprobante ${m.numero_comprobante}`, m.banco, m.notas].filter(Boolean).join(' · ');
                  return (
                    <tr key={m.id}>
                      <td className="px-4 py-3 whitespace-nowrap">{dayjs(m.fecha).format('DD/MM/YYYY')}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${c}`}>{t}</span></td>
                      <td className="px-4 py-3 text-gray-700">{detalle || '—'}<span className="block text-[11px] text-gray-400">{m.creado_por || ''}</span></td>
                      <td className={`px-4 py-3 text-right whitespace-nowrap ${entra ? 'text-emerald-700' : ''}`}>{entra ? '+' : '−'}{fmt(m.monto, moneda)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">{fmt(corrido, moneda)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
              <p className="sm:col-span-2 text-gray-600">Si el cliente prefiere que le devolvamos la plata en vez de dejarla a favor. Máximo {fmt(disponible, moneda)}.</p>
              <div><label className="block font-medium text-gray-700 mb-1">Monto a devolver *</label>
                <input type="number" step="0.01" value={dev.monto} onChange={(e) => setDev({ ...dev, monto: e.target.value })} className="w-full border rounded-lg px-3 py-2" /></div>
              <div><label className="block font-medium text-gray-700 mb-1">Fecha *</label>
                <input type="date" value={dev.fecha} onChange={(e) => setDev({ ...dev, fecha: e.target.value })} className="w-full border rounded-lg px-3 py-2" /></div>
              <div><label className="block font-medium text-gray-700 mb-1">Medio</label>
                <select value={dev.medioPago} onChange={(e) => setDev({ ...dev, medioPago: e.target.value })} className="w-full border rounded-lg px-3 py-2">{MEDIOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></div>
              <div><label className="block font-medium text-gray-700 mb-1">Banco</label>
                <select value={dev.banco} onChange={(e) => setDev({ ...dev, banco: e.target.value })} className="w-full border rounded-lg px-3 py-2"><option value="">-- Seleccionar --</option>{bancos.map(b => <option key={b.id} value={b.nombre}>{b.nombre}</option>)}</select></div>
              <div><label className="block font-medium text-gray-700 mb-1">N° Comprobante</label>
                <input type="text" value={dev.numeroComprobante} onChange={(e) => setDev({ ...dev, numeroComprobante: e.target.value })} className="w-full border rounded-lg px-3 py-2" /></div>
              <div><label className="block font-medium text-gray-700 mb-1">Nota</label>
                <input type="text" value={dev.notas} onChange={(e) => setDev({ ...dev, notas: e.target.value })} className="w-full border rounded-lg px-3 py-2" /></div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t bg-gray-50">
          <span className="text-sm text-gray-600">
            {vista === 'aplicar' && <>Aplicado: <b>{fmt(totalAplicar, moneda)}</b> · Queda a favor: <b className="text-emerald-700">{fmt(Math.max(0, disponible - totalAplicar), moneda)}</b></>}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100">Cerrar</button>
            {vista === 'aplicar' && pendientes.length > 0 && (
              <button onClick={aplicar} disabled={trabajando || totalAplicar <= 0} className="px-4 py-2 border-2 border-emerald-600 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-2">
                {trabajando && <Loader2 className="h-4 w-4 animate-spin" />} Aplicar {fmt(totalAplicar, moneda)}
              </button>
            )}
            {vista === 'devolver' && (
              <button onClick={devolver} disabled={trabajando || disponible <= 0} className="px-4 py-2 border-2 border-red-400 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 flex items-center gap-2">
                {trabajando && <Loader2 className="h-4 w-4 animate-spin" />} Registrar devolución
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
